/* eslint-disable max-lines */
import { SparklesIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import {
  FormProvider, SubmitHandler, useFieldArray, useForm
} from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import useFetch from "../../../hooks/useFetch";
import { IAIContentResponseDto } from "../../../interfaces/endpoints/IAIContent";
import { ICityPageSectionEditDto } from "../../../interfaces/endpoints/ICityPage";
import {
  editCityPageSections, getCityPageSections
} from "../../../services/CityPages.service";
import AIGenerateModal from "../../line-pages/edit/AIGenerateModal";
import FormWrapper from "../../ui/FormWrapper";
import SpinnerLoader from "../../ui/SpinnerLoader";
import SectionItem from "./SectionItem";

type FormValues = { sections: ICityPageSectionEditDto[] };

const EditCityPageSections = () => {
  const { id } = useParams<{ id: string }>();
  const [showAIModal, setShowAIModal] = useState(false);
  const methods = useForm<FormValues>({ defaultValues: { sections: [] } });
  const { control, handleSubmit, reset, watch } = methods;
  const { append, move } = useFieldArray({ control, name: "sections" });
  const dragItem = useRef<number | null>(null);

  const { response, loading, refetch } = useFetch(
    () => getCityPageSections(Number(id)),
    { initRequest: true }
  );

  useEffect(() => {
    if (response?.data) reset({ sections: response.data });
  }, [response, reset]);

  const sections = watch("sections") || [];

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const ordered = data.sections.map((s, i) => ({
        ...s, displayOrder: i, entityName: "CityPage", entityId: Number(id)
      }));
      await editCityPageSections(Number(id), ordered);
      toast.success("Seksionet u ruajtën me sukses.", { position: "top-right" });
      refetch();
    } catch {
      toast.error("Ndodhi një gabim gjatë ruajtjes.", { position: "top-right" });
    }
  };

  const addSection = () => {
    append({
      id: 0, displayOrder: sections.length,
      locales: [
        { languageId: 1, title: "", content: "" },
        { languageId: 2, title: "", content: "" }
      ],
      sectionPictureMapping: []
    });
  };

  const handleDeleteSection = (sectionId: number, index: number) => {
    const updatedSections =
      sectionId === 0
        ? sections.filter((_, i) => i !== index)
        : sections.filter((section) => section.id !== sectionId);
    reset({ sections: updatedSections });
  };

  const handleAIGenerated = (aiResponse: IAIContentResponseDto) => {
    aiResponse.sections.forEach((aiSection) => {
      append({
        id: 0,
        displayOrder: sections.length,
        locales: aiSection.locales.map((l) => ({
          languageId: l.languageId,
          title: l.title,
          content: l.content
        })),
        sectionPictureMapping: []
      });
    });
  };

  return (
    <FormProvider {...methods}>
      <FormWrapper
        onSubmit={handleSubmit(onSubmit)}
        title="Modifiko Seksionet e Qytetit"
        contentClassName="max-w-7xl mx-auto"
        showPrompt={
          !methods.formState.isSubmitSuccessful &&
          !!Object.keys(methods.formState.dirtyFields).length
        }
      >
        <div className="min-h-[600px] flex items-center justify-center">
          {loading ? (
            <SpinnerLoader className="text-primary" />
          ) : (
            <div className="w-full">
              <div className="w-full mb-6">
                {sections.map((section, i) => (
                  <SectionItem
                    key={section.id || i}
                    index={i}
                    section={section}
                    onDelete={() => handleDeleteSection(section.id, i)}
                    onDragStart={(e) => {
                      dragItem.current = i;
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (dragItem.current === null) return;
                      if (dragItem.current === i) return;
                      move(dragItem.current, i);
                      dragItem.current = i;
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      dragItem.current = null;
                    }}
                  />
                ))}
              </div>
              <div className="flex flex-row justify-center items-center gap-4 mt-6">
                <button
                  type="button"
                  onClick={addSection}
                  className={
                    "p-2 px-4 font-semibold bg-primary text-white "
                    + "rounded-xl shadow-sm hover:bg-opacity-90 transition-all"
                  }
                >
                  Shto Seksion
                </button>
                <button
                  type="button"
                  className={
                    "flex items-center gap-2 px-4 py-2 bg-primary text-white "
                    + "rounded-xl hover:bg-opacity-90 transition-all font-semibold shadow-sm"
                  }
                  onClick={() => setShowAIModal(true)}
                >
                  <SparklesIcon className="w-5 h-5 text-white" />
                  Gjenero me AI
                </button>
              </div>
            </div>
          )}
        </div>
        <AIGenerateModal
          show={showAIModal}
          onClose={() => setShowAIModal(false)}
          entityId={Number(id)}
          pageType={1}
          existingSections={sections}
          onGenerated={handleAIGenerated}
        />
      </FormWrapper>
    </FormProvider>
  );
};

export default EditCityPageSections;
